'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from '@kit/ui/utils';

interface BlurUpImageProps {
  /** Transformed image src (R2 cdn-cgi/image URL or Supabase passthrough). */
  src: string;
  /** Optional responsive srcSet. */
  srcSet?: string;
  /** Optional sizes attribute paired with srcSet. */
  sizes?: string;
  /** Accessible alt text. */
  alt: string;
  /**
   * Tiny blurred LQIP placeholder URL, or null when unavailable
   * (e.g. Supabase-signed sources). When null, only the neutral
   * `bg-secondary` panel shows until the image loads.
   */
  placeholder: string | null;
  /** Loading strategy for the underlying <img>. Defaults to lazy. */
  loading?: 'lazy' | 'eager';
  /** Decoding hint for the underlying <img>. Defaults to async. */
  decoding?: 'async' | 'sync' | 'auto';
  /**
   * LCP priority hint for the underlying <img> (BAL-625). Pass `'high'` for
   * the above-the-fold hero tile so the browser fetches it ahead of lazy
   * gallery tiles. Omitted → no hint (browser default).
   */
  fetchPriority?: 'high' | 'auto';
  /**
   * Hide the wrapper from assistive tech (decorative images). When true
   * the real <img> is also marked aria-hidden so screen readers skip it.
   */
  ariaHidden?: boolean;
  /** Class for the wrapper element. */
  className?: string;
  /** Class for the <img> element. */
  imgClassName?: string;
}

/**
 * Renders an image with a CDN blur-up placeholder.
 *
 * While the real image loads, a heavily-blurred tiny variant
 * (`placeholder`) is shown scaled up behind a neutral `bg-secondary`
 * panel. Once the real image fires `onLoad`, it fades in over the blur.
 * When `placeholder` is null, behaves like a plain image over the
 * neutral panel (no regression vs. the previous bare <img>).
 *
 * @see docs/architecture/image-rendering.md
 */
export function BlurUpImage({
  src,
  srcSet,
  sizes,
  alt,
  placeholder,
  loading = 'lazy',
  decoding = 'async',
  fetchPriority,
  ariaHidden,
  className,
  imgClassName,
}: BlurUpImageProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  // The <img> is server-rendered into the initial HTML (this is a client
  // component, so Next.js SSRs it with `opacity-0`). A cached or fast image —
  // e.g. the immutable AVIF gallery transforms, which Cloudflare serves as a
  // ~3KB cache HIT — can fire its `load` event BEFORE React hydrates and
  // attaches `onLoad`. When that happens `onLoad` never runs and the image is
  // stuck at `opacity-0` behind the blur placeholder forever (the reported
  // "gallery shows only blur" bug). Detect the already-complete image on mount
  // and reveal it; `onLoad` still covers the not-yet-loaded case.
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setLoaded(true);
  }, []);

  return (
    <div
      aria-hidden={ariaHidden ? true : undefined}
      className={cn('bg-secondary relative overflow-hidden', className)}
    >
      {placeholder && !loaded ? (
        <div
          aria-hidden
          className="absolute inset-0 scale-110 bg-cover bg-center blur-xl"
          style={{ backgroundImage: `url(${placeholder})` }}
        />
      ) : null}

      {/* eslint-disable-next-line @next/next/no-img-element -- R2 cdn-cgi/image
          transforms are applied via cdnImageUrl/cdnImageSrcSet, not next/image. */}
      <img
        ref={imgRef}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        aria-hidden={ariaHidden ? true : undefined}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        onLoad={() => setLoaded(true)}
        // A broken image (404/decode error) must still reveal so it doesn't
        // masquerade as a permanent blur placeholder.
        onError={() => setLoaded(true)}
        className={cn(
          // `transition` (all properties) so callers can add transform
          // animations (e.g. hover scale) without clobbering the opacity fade.
          'relative h-full w-full object-cover transition duration-300',
          loaded ? 'opacity-100' : 'opacity-0',
          imgClassName,
        )}
      />
    </div>
  );
}
